import {
  APIReferenceAttributes,
  ArdoqId,
  LinkedComponent,
} from '@ardoq/api-types';
import {
  getEmptyComponentGraphData,
  GlobalReferenceTypeId,
  ModelTypes,
  SimpleReferenceModel,
} from '@ardoq/data-model';
import { getCurrentLocale, localeCompare } from '@ardoq/locale';

export const generateGraphData = ({
  references,
  typesByModelId,
}: {
  references: APIReferenceAttributes[];
  typesByModelId: Record<ArdoqId, ModelTypes>;
}) => {
  const sourceMap = new Map<ArdoqId, LinkedComponent[]>();
  const targetMap = new Map<ArdoqId, LinkedComponent[]>();
  const referenceTypeMap = new Map<string, GlobalReferenceTypeId[]>();
  const referenceMap = new Map<ArdoqId, SimpleReferenceModel>();

  references.forEach(
    ({
      _id: referenceId,
      source: sourceId,
      target: targetId,
      rootWorkspace,
      model,
      type,
    }: APIReferenceAttributes) => {
      const referenceType = typesByModelId[model]?.referenceTypesById[type];

      referenceMap.set(referenceId, { sourceId, targetId });

      if (referenceType) {
        const referenceTypeId = `${rootWorkspace}-${type}`;
        const referenceTypeName = referenceType.name;
        let typeIds = referenceTypeMap.get(referenceTypeName);
        if (!typeIds) {
          typeIds = [];
          referenceTypeMap.set(referenceTypeName, typeIds);
        }
        if (!typeIds.includes(referenceTypeId)) {
          typeIds.push(referenceTypeId);
        }
      }
      let targetIds = sourceMap.get(sourceId);
      if (!targetIds) {
        targetIds = [];
        sourceMap.set(sourceId, targetIds);
      }
      targetIds.push({ componentId: targetId, referenceId });
      let sourceIds = targetMap.get(targetId);
      if (!sourceIds) {
        sourceIds = [];
        targetMap.set(targetId, sourceIds);
      }
      sourceIds.push({ componentId: sourceId, referenceId });
    }
  );

  const referenceTypes = Array.from(referenceTypeMap)
    .map(([name, typeIds]) => ({ name, typeIds }))
    .sort((a, b) => localeCompare(a.name, b.name, getCurrentLocale()));

  sourceMap.forEach(list => Object.freeze(list));
  targetMap.forEach(list => Object.freeze(list));
  referenceMap.forEach(list => Object.freeze(list));

  return {
    ...getEmptyComponentGraphData(),
    sourceMap,
    targetMap,
    referenceMap,
    referenceTypes,
  };
};
